import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import { AlertCircle } from '@saleshandy/icons';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { NumericFormat } from 'react-number-format';
import { FilterComponentType } from '../../../types';
import { Placement } from '../../../../../shared/design-system/components/overlay';
import Checkbox from '../../../../../shared/design-system/components/atoms/checkbox';
import { setFilterValue } from '../../../v2-lead-slice';

const MAX_LIMIT_VALUE = 100;

const validateLimitValue = (
  value: string,
): { isInvalid: boolean; errorMessage: string } => {
  const trimmedValue = value?.trim() || '';
  if (trimmedValue === '') {
    return { isInvalid: false, errorMessage: '' };
  }
  if (!/^\d+$/.test(trimmedValue)) {
    return {
      isInvalid: true,
      errorMessage: 'Please enter a valid positive number',
    };
  }
  const numValue = Number(trimmedValue);
  if (numValue <= 0) {
    return {
      isInvalid: true,
      errorMessage: 'Please enter a number greater than 0',
    };
  }
  if (numValue > MAX_LIMIT_VALUE) {
    return {
      isInvalid: true,
      errorMessage: `Maximum value is ${MAX_LIMIT_VALUE.toLocaleString(
        'en-US',
      )}`,
    };
  }
  return { isInvalid: false, errorMessage: '' };
};

const Autocomplete = (props) => {
  const {
    isExpand,
    placeholder,
    filterKey,
    name,
    className,
    handleFoundValueExceptFromList,
    handleQuery,
    applyPendingFilters,
    advanceFilters,
    pendingFilters,
    onLimitValidationChange,
  } = props;
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const inputRef = useRef(null);
  const dispatch = useDispatch();

  // For exact match option - initialize from URL params
  const [exactMatch, setExactMatch] = useState<boolean>(
    queryParams.get(`${filterKey}_exactMatch`) === '1',
  );

  // For limit filter validation
  const [isInvalidInput, setIsInvalidInput] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const pendingInputValue = pendingFilters.get(filterKey) || '';
  const [limitInputValue, setLimitInputValue] = useState(pendingInputValue);

  const inputValue =
    filterKey === 'limit' ? limitInputValue : pendingInputValue;

  const applyLimitValidation = (value: string) => {
    const validation = validateLimitValue(value);
    setIsInvalidInput(validation.isInvalid);
    setErrorMessage(validation.errorMessage);
    onLimitValidationChange?.(validation.isInvalid);
    return validation;
  };

  const getTooltip = (text) => (
    <Tooltip id="popover-basic" className="bs-tooltip-inner">
      {text}
    </Tooltip>
  );

  const onKeyDown = (e) => {
    if (e.keyCode !== 13) {
      return;
    }

    const rawValue = e.target?.value ?? '';
    const trimmedValue = rawValue.trim();

    if (trimmedValue === '') {
      setExactMatch(false);
      if (applyPendingFilters) {
        handleQuery({
          filterKey,
          filterValue: '',
          filterType: FilterComponentType.SEARCH_INPUT,
          showExactMatch: false,
          onFiltersUpdated: (updatedFilters) => {
            applyPendingFilters(updatedFilters);
          },
        });
      } else {
        handleQuery({
          filterKey,
          filterValue: '',
          filterType: FilterComponentType.SEARCH_INPUT,
          showExactMatch: false,
        });
      }
      return;
    }

    // For single-value filters (full_name, social_urls, website, etc.), trigger search API immediately on Enter
    if (applyPendingFilters) {
      handleQuery({
        filterKey,
        filterValue: rawValue,
        filterType: FilterComponentType.SEARCH_INPUT,
        onFiltersUpdated: (updatedFilters) => {
          applyPendingFilters(updatedFilters);
        },
      });
      return;
    }

    handleQuery({
      filterKey,
      filterValue: rawValue,
      filterType: FilterComponentType.SEARCH_INPUT,
    });
  };

  // Handle NumericFormat value change for limit filter
  const handleLimitValueChange = (values: any) => {
    const newValue = values?.value || '';
    setLimitInputValue(newValue);

    const trimmedValue = newValue?.trim();
    const validation = applyLimitValidation(newValue);

    if (trimmedValue === '') {
      if (applyPendingFilters) {
        handleQuery({
          filterKey,
          filterValue: '',
          filterType: FilterComponentType.SEARCH_INPUT,
          onFiltersUpdated: (updatedFilters) => {
            applyPendingFilters(updatedFilters);
          },
        });
      } else {
        handleQuery({
          filterKey,
          filterValue: '',
          filterType: FilterComponentType.SEARCH_INPUT,
        });
      }
    } else if (!validation.isInvalid) {
      handleQuery({
        filterKey,
        filterValue: trimmedValue,
        filterType: FilterComponentType.SEARCH_INPUT,
      });
    }
  };

  // Handle Enter key press for NumericFormat
  const handleInputKeyPress = (e) => {
    if (e.key === 'Enter' && filterKey === 'limit') {
      const trimmedValue = limitInputValue?.trim();

      if (trimmedValue === '') {
        return;
      }

      const validation = applyLimitValidation(limitInputValue);
      if (validation.isInvalid) {
        return;
      }

      handleQuery({
        filterKey,
        filterValue: trimmedValue,
        filterType: FilterComponentType.SEARCH_INPUT,
        onFiltersUpdated: (updatedFilters) => {
          applyPendingFilters(updatedFilters);
        },
      });
    }
  };

  const handleInputChange = (e) => {
    const value = e?.target?.value ?? '';
    const trimmedValue = value.trim();

    // Prevent API/query updates for excessively long input
    if (trimmedValue.length > 50) {
      setIsInvalidInput(true);
      setErrorMessage('Company name must be at most 50 characters.');
      return;
    }
    setIsInvalidInput(false);
    setErrorMessage('');

    if (trimmedValue === '') {
      if (applyPendingFilters) {
        handleQuery({
          filterKey,
          filterValue: '',
          filterType: FilterComponentType.SEARCH_INPUT,
          showExactMatch: false,
          onFiltersUpdated: (updatedFilters) => {
            applyPendingFilters(updatedFilters);
          },
        });
      } else {
        handleQuery({
          filterKey,
          filterValue: '',
          filterType: FilterComponentType.SEARCH_INPUT,
          showExactMatch: false,
        });
      }
      setExactMatch(false);
    } else {
      handleQuery({
        filterKey,
        filterValue: value,
        filterType: FilterComponentType.SEARCH_INPUT,
      });
    }
  };

  // Validate limit input on blur
  const handleBlur = () => {
    if (filterKey === 'limit') {
      applyLimitValidation(limitInputValue);
    }
  };

  useEffect(() => {
    if (filterKey !== 'limit') return;
    setLimitInputValue(pendingInputValue);
    applyLimitValidation(pendingInputValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingInputValue, filterKey]);

  useEffect(() => {
    if (filterKey !== 'limit' || !onLimitValidationChange) return undefined;
    return () => {
      onLimitValidationChange(false);
    };
  }, [filterKey, onLimitValidationChange]);

  useEffect(() => {
    let initValue = '';
    if (['location', 'department', 'industry'].includes(filterKey)) {
      initValue = handleFoundValueExceptFromList(filterKey);
    } else {
      initValue = queryParams.get(filterKey) || '';
    }
    if (filterKey === 'limit') {
      setLimitInputValue(initValue || '');
      applyLimitValidation(initValue || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.search]);

  useEffect(() => {
    if (
      inputRef &&
      isExpand &&
      inputRef.current &&
      filterKey !== 'sic_codes' &&
      filterKey !== 'naics_codes'
    ) {
      inputRef.current.focus();
    }
  }, [isExpand]);

  // for handling show exact match
  const handleShowExactMatch = (checked: boolean) => {
    handleQuery({
      filterKey,
      filterValue: inputValue,
      filterType: FilterComponentType.SEARCH_INPUT,
      showExactMatch: checked,
    });
    setExactMatch(checked);
  };

  // for handling show exact match in referesh or location changes in url
  useEffect(() => {
    const exactMatchParam = queryParams.get(`${filterKey}_exactMatch`);
    if (exactMatchParam === '1') {
      setExactMatch(true);
    } else {
      setExactMatch(false);
    }
  }, [location?.search, filterKey]);

  useEffect(() => {
    const debouncedDispatch = debounce((value) => {
      dispatch(setFilterValue(value));
    }, 300);
    debouncedDispatch(inputValue);

    return () => {
      debouncedDispatch.cancel();
    };
  }, [inputValue]);

  return (
    <div className={`input-container ${className}`}>
      {name && <p className="blue-txt-15 label mb-1">{name}</p>}
      <div className={filterKey === 'limit' ? 'd-flex align-items-center' : ''}>
        {filterKey === 'limit' ? (
          <div className="max-input v2-lead-limit-input">
            <NumericFormat
              getInputRef={inputRef}
              value={inputValue}
              placeholder={placeholder}
              autoComplete="off"
              id={`filter-${filterKey}-input`}
              thousandSeparator={true}
              allowNegative={false}
              decimalScale={0}
              onValueChange={handleLimitValueChange}
              onKeyPress={handleInputKeyPress}
              onBlur={handleBlur}
              className={`form-control blue-txt-15 regular-2 ${
                isInvalidInput ? ' is-invalid' : ''
              }`}
            />
            {isInvalidInput && (
              <div className="error-message">
                <OverlayTrigger
                  placement={Placement.Bottom}
                  overlay={getTooltip(
                    errorMessage || 'Please enter a valid positive number',
                  )}
                >
                  <AlertCircle className="red-txt-16" width={16} height={16} />
                </OverlayTrigger>
              </div>
            )}
          </div>
        ) : (
          <input
            ref={inputRef}
            type="text"
            autoComplete="off"
            id={`filter-${filterKey}-input`}
            className="auto-search-input"
            onChange={handleInputChange}
            onKeyDown={onKeyDown}
            value={inputValue}
            placeholder={placeholder}
            maxLength={200}
          />
        )}
        {/* Show validation error for overly long input */}
        {/* {isInvalidInput && errorMessage && (
          <div className="error-message mt-1">
            <OverlayTrigger
              placement={Placement.Bottom}
              overlay={getTooltip(errorMessage)}
            >
              <AlertCircle className="red-txt-16" width={16} height={16} />
            </OverlayTrigger>
          </div>
        )} */}
      </div>
      {advanceFilters?.showExactMatchCheckbox && (
        <Checkbox
          value={filterKey}
          className="w-100 show-exact-match-check"
          onChange={(checked) => {
            setExactMatch(checked);
            handleShowExactMatch(checked);
          }}
          checked={exactMatch}
          disabled={inputValue?.trim() === ''}
          label={
            <div className="label-container d-inline-block mt-2">
              <p className="checkbox-label m-0">Show Exact Match</p>
            </div>
          }
        />
      )}
    </div>
  );
};
export default Autocomplete;
